/**
 * Utilitário para encontrar a primeira rota acessível baseada em permissões
 * 
 * Retorna a primeira rota do ERP que o usuário tem permissão para acessar
 */

import { MODULES, ACTIONS } from '../hooks/usePermissions'
import { canAccessErpReport, canAccessReportsHub } from './permissions'

/**
 * Mapeamento de rotas para permissões (mesma estrutura do Sidebar)
 */
const ROUTE_PERMISSIONS = {
  '/erp/cockpit': { module: MODULES.COCKPIT, action: ACTIONS.VIEW },
  '/erp/dashboard': { module: MODULES.DASHBOARD, action: ACTIONS.VIEW },
  '/erp/pessoas/clientes': { module: MODULES.PEOPLE, action: ACTIONS.PEOPLE_CUSTOMERS },
  '/erp/pessoas/fornecedores': { module: MODULES.PEOPLE, action: ACTIONS.PEOPLE_SUPPLIERS },
  '/erp/pessoas/usuarios': { module: MODULES.PEOPLE, action: ACTIONS.PEOPLE_USERS },
  '/erp/pessoas/grupos': { module: MODULES.PEOPLE, action: ACTIONS.PEOPLE_ACCESS_GROUPS },
  '/erp/produtos/produtos': { module: MODULES.PRODUCTS, action: ACTIONS.VIEW },
  '/erp/produtos/grades': { module: MODULES.PRODUCTS, action: ACTIONS.PRODUCTS_GRIDS },
  '/erp/produtos/grupos': { module: MODULES.PRODUCTS, action: ACTIONS.PRODUCTS_GROUPS },
  '/erp/produtos/marcas': { module: MODULES.PRODUCTS, action: ACTIONS.PRODUCTS_BRANDS },
  '/erp/produtos/unidades': { module: MODULES.PRODUCTS, action: ACTIONS.PRODUCTS_UNITS },
  '/erp/estoque/movimentacoes': { module: MODULES.STOCK, action: ACTIONS.STOCK_MOVEMENTS },
  '/erp/estoque/trocas': { module: MODULES.STOCK, action: ACTIONS.STOCK_MOVEMENTS },
  '/erp/vendas/vendas': { module: MODULES.SALES, action: ACTIONS.SALES_PDV_VIEW },
  '/erp/vendas/relacao-vendas': { module: MODULES.SALES, action: ACTIONS.SALES_RELATION_VIEW },
  '/erp/vendas/pre-venda': { module: MODULES.SALES, action: ACTIONS.SALES_PRESALE },
  '/erp/vendas/relacao-pre-venda': { module: MODULES.SALES, action: ACTIONS.SALES_PRESALE_LIST_VIEW },
  '/erp/vendas/online': { module: MODULES.SALES, action: ACTIONS.SALES_ONLINE_VIEW },
  '/erp/vendas/nfe': { module: MODULES.SALES, action: ACTIONS.SALES_NFE_VIEW },
  '/erp/vendas/tabelas-especiais': { module: MODULES.SALES, action: ACTIONS.SALES_SPECIAL_TABLES_VIEW },
  '/erp/vendas/caixa': { module: MODULES.SALES, action: ACTIONS.SALES_CASH_REGISTER_RECONCILIATION },
  '/erp/orcamentos': { module: MODULES.QUOTES, action: ACTIONS.VIEW },
  '/erp/orcamentos/dashboard': { module: MODULES.QUOTES, action: ACTIONS.VIEW },
  '/erp/orcamentos/novo': { module: MODULES.QUOTES, action: ACTIONS.CREATE },
  '/erp/financeiro/contas-receber': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_RECEIVABLES_VIEW },
  '/erp/financeiro/boletos/dashboard': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_BOLETOS_DASHBOARD },
  '/erp/financeiro/boletos': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_BOLETOS_VIEW },
  '/erp/purchases/finance': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_PURCHASE_APPROVAL },
  '/erp/financeiro/dre': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_DRE },
  '/erp/financeiro/dfc': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_DFC },
  '/erp/financeiro/balanco': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_BALANCE_SHEET },
  '/erp/financeiro/contas-pagar': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_PAYABLES },
  '/erp/financeiro/extrato-caixa': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_CASH_FLOW },
  '/erp/financeiro/conciliacao-cartoes': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_CARD_RECONCILIATION },
  '/erp/financeiro/contas': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_ACCOUNTS },
  '/erp/financeiro/categorias': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_CATEGORIES },
  '/erp/financeiro/centros-custo': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_COST_CENTERS },
  '/erp/financeiro/formas-recebimento': { module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_RECEIPT_METHODS },
  '/erp/relatorios': { module: MODULES.REPORTS, action: ACTIONS.VIEW },
  '/erp/relatorios/contas-receber': { module: MODULES.REPORTS, action: ACTIONS.REPORTS_RECEIVABLES },
  '/erp/relatorios/estoque': { module: MODULES.REPORTS, action: ACTIONS.REPORTS_STOCK },
  '/erp/relatorios/vendas': { module: MODULES.REPORTS, action: ACTIONS.REPORTS_SALES },
  '/erp/configuracoes': { module: MODULES.SETTINGS, action: ACTIONS.VIEW },
  '/erp/configuracoes/cockpit': { module: MODULES.SETTINGS, action: ACTIONS.VIEW },
  '/erp/configuracoes/hero': { module: MODULES.SETTINGS, action: ACTIONS.VIEW },
  '/erp/configuracoes/vitrine': { module: MODULES.SETTINGS, action: ACTIONS.VIEW },
  '/erp/configuracoes/frete-cep': { module: MODULES.SETTINGS, action: ACTIONS.VIEW },
  '/erp/documents/dashboard': { module: MODULES.DOCUMENTS, action: ACTIONS.DOCUMENTS_DASHBOARD },
  '/erp/documents/company': { module: MODULES.DOCUMENTS, action: ACTIONS.DOCUMENTS_COMPANY_VIEW },
  '/erp/documents/users': { module: MODULES.DOCUMENTS, action: ACTIONS.DOCUMENTS_USER_VIEW },
  '/erp/documents/categories': { module: MODULES.DOCUMENTS, action: ACTIONS.DOCUMENTS_CATEGORIES_VIEW },
  '/erp/documents/attendance-reports': { module: MODULES.ATTENDANCE, action: ACTIONS.ATTENDANCE_ACCOUNTING },
  '/erp/documents/attendance-log': { module: MODULES.ATTENDANCE, action: ACTIONS.ATTENDANCE_LOG },
  '/erp/ponto-eletronico': { module: MODULES.ATTENDANCE, action: ACTIONS.ATTENDANCE_VIEW },
  '/erp/purchases': { module: MODULES.PURCHASES, action: ACTIONS.PURCHASES_DASHBOARD },
  '/erp/prospecting': { module: MODULES.PROSPECTING, action: ACTIONS.PROSPECTING_VIEW },
  '/erp/mensagens': { module: MODULES.MESSAGES, action: ACTIONS.VIEW },
  '/erp/pacientes': { module: MODULES.PATIENTS, action: ACTIONS.VIEW },
  '/erp/pacientes/painel': { module: MODULES.PATIENTS, action: ACTIONS.PATIENTS_DASHBOARD },
  '/erp/plantoes': { module: MODULES.DUTY_ROSTERS, action: ACTIONS.VIEW },
  '/erp/minha-escala': { module: MODULES.MY_SHIFTS, action: ACTIONS.VIEW },
}

/**
 * Ordem de prioridade das rotas (ordem de verificação)
 * Para usuários comuns (não admin), cockpit é a primeira opção
 */
const ROUTE_PRIORITY = [
  '/erp/cockpit', // Primeira opção para usuários comuns
  '/erp/dashboard',
  '/erp/pacientes',
  '/erp/pacientes/painel',
  '/erp/plantoes',
  '/erp/minha-escala',
  '/erp/pessoas/clientes',
  '/erp/produtos/produtos',
  '/erp/produtos/grades',
  '/erp/vendas/vendas',
  '/erp/estoque/movimentacoes',
  '/erp/financeiro/contas-pagar',
  '/erp/pessoas/fornecedores',
  '/erp/produtos/grupos',
  '/erp/produtos/marcas',
  '/erp/produtos/unidades',
  '/erp/vendas/relacao-vendas',
  '/erp/vendas/pre-venda',
  '/erp/orcamentos',
  '/erp/orcamentos/dashboard',
  '/erp/financeiro/contas-receber',
  '/erp/financeiro/extrato-caixa',
  '/erp/financeiro/contas',
  '/erp/financeiro/categorias',
  '/erp/financeiro/centros-custo',
  '/erp/financeiro/formas-recebimento',
  '/erp/financeiro/conciliacao-cartoes',
  '/erp/relatorios',
  '/erp/relatorios/vendas',
  '/erp/relatorios/estoque',
  '/erp/relatorios/contas-receber',
  '/erp/pessoas/usuarios',
  '/erp/pessoas/grupos',
  '/erp/documents/dashboard',
  '/erp/documents/company',
  '/erp/documents/users',
  '/erp/documents/categories',
  '/erp/documents/attendance-reports',
  '/erp/documents/attendance-log',
  '/erp/ponto-eletronico',
  '/erp/purchases',
  '/erp/prospecting',
  '/erp/mensagens'
]

function canOpenRoute(route, hasPermission) {
  const permission = ROUTE_PERMISSIONS[route]
  if (!permission) return false
  return hasPermission(permission.module, permission.action)
}

/**
 * Encontra a primeira rota acessível baseada nas permissões do usuário
 * 
 * @param {Function} hasPermission - Função para verificar permissão (module, action)
 * @param {boolean} isAdmin - Se o usuário é admin
 * @param {boolean} relative - Se true, retorna rota relativa (sem /erp/), se false retorna completa
 * @returns {string|null} - Primeira rota acessível ou null se nenhuma for acessível
 */
export function getFirstAccessibleRoute(hasPermission, isAdmin, relative = false) {
  // Admin sempre pode acessar dashboard
  const dashboardRoute = relative ? 'dashboard' : '/erp/dashboard'
  // Para usuários comuns, cockpit é a primeira opção
  const cockpitRoute = relative ? 'cockpit' : '/erp/cockpit'
  if (isAdmin) {
    return dashboardRoute
  }
  
  // Verificar cockpit primeiro para usuários comuns
  if (hasPermission(MODULES.COCKPIT, ACTIONS.VIEW)) {
    return cockpitRoute
  }

  // Verificar rotas na ordem de prioridade
  for (const route of ROUTE_PRIORITY) {
    if (!canOpenRoute(route, hasPermission)) continue

    if (relative) {
      return route.replace('/erp/', '')
    }
    return route
  }

  // Se nenhuma rota específica for acessível, retornar null
  return null
}

/**
 * Verifica se uma rota específica é acessível
 * 
 * @param {string} route - Rota para verificar
 * @param {Function} hasPermission - Função para verificar permissão
 * @param {boolean} isAdmin - Se o usuário é admin
 * @returns {boolean} - Se a rota é acessível
 */
export function isRouteAccessible(route, hasPermission, isAdmin) {
  if (isAdmin) return true

  if (route === '/erp/pacientes/painel') {
    return hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_DASHBOARD)
  }

  if (route === '/erp/pacientes' || route?.startsWith('/erp/pacientes/')) {
    return hasPermission(MODULES.PATIENTS, ACTIONS.VIEW)
  }

  if (route === '/erp/configuracoes/certificados') {
    return (
      hasPermission(MODULES.DIGITAL_CERTIFICATE, ACTIONS.DIGITAL_CERTIFICATE_MANAGE) ||
      hasPermission(MODULES.SETTINGS, ACTIONS.SETTINGS_CERTIFICATES)
    )
  }

  if (route?.startsWith('/erp/configuracoes')) {
    return hasPermission(MODULES.SETTINGS, ACTIONS.VIEW)
  }

  if (route === '/erp/relatorios' || route?.startsWith('/erp/relatorios/')) {
    if (route === '/erp/relatorios/contas-receber' || route?.startsWith('/erp/relatorios/contas-receber')) {
      return canAccessErpReport(hasPermission, ACTIONS.REPORTS_RECEIVABLES)
    }
    if (route === '/erp/relatorios/estoque' || route?.startsWith('/erp/relatorios/estoque')) {
      return canAccessErpReport(hasPermission, ACTIONS.REPORTS_STOCK)
    }
    if (route === '/erp/relatorios/vendas' || route?.startsWith('/erp/relatorios/vendas')) {
      return canAccessErpReport(hasPermission, ACTIONS.REPORTS_SALES)
    }
    return canAccessReportsHub(hasPermission)
  }

  return canOpenRoute(route, hasPermission)
}

